import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tab,
  Tabs,
  Tooltip
} from "@material-ui/core";
import Viewer from "react-viewer";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import AudioGalleryModal from "../AudioGalleryModal";
import audioIcon from "../../assets/miscPng/audio-waves.png";
import docIcon from "../../assets/miscPng/google-docs.png";
import videoIcon from "../../assets/miscPng/video.png";

const useStyles = makeStyles(theme => ({
  //  *************
  //  ** Content **
  //  *************
  dialogContent: {
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  //  **********************
  //  ** Medias Container **
  //  **********************
  mediasList: {
    display: "flex",
		flexDirection: "row",
    flexWrap: "wrap",
		alignItems: "center",
		justifyContent: "space-around",
		gap: "1em",
    marginTop: "1em",
  },

  mediaItem: {
    borderRadius: "5px",
		width: "75px",
		height: "75px",
		cursor: "pointer",
		transition: "all 0.3s ease",
		userDrag: "none",
    marginTop: "1em",

		"&:hover": {
			opacity: "0.9",
			transform: "translateY(-5px)",
		},
  },

  mediaItemSpan: {
    borderRadius: "5px",
    border: `2px solid ${theme.palette.primary.pastel}`,
		width: "100px",
		height: "100px",
		cursor: "pointer",
		transition: "all 0.3s ease",
		userDrag: "none",
    padding: "5px",
    marginTop: "1em",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",

		"&:hover": {
			opacity: "0.9",
			transform: "translateY(-5px)",
		},
  },

  fileIcon: {
    borderRadius: "5px",
		width: "75px",
		height: "75px",
		cursor: "pointer",
    userDrag: "none",
  },
}));

const MediasGalleryModal = ({ open, onClose, contactId }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const [mediasTab, setMediasTab] = useState("images");

  const [mediasListImages, setMediasListImages] = useState([]);
  const [mediasListVideos, setMediasListVideos] = useState([]);
  const [mediasListAudios, setMediasListAudios] = useState([]);
  const [mediasListOthers, setMediasListOthers] = useState([]);

  const [viewerVisible, setViewerVisible] = useState(false);
	const [viewerImageUrl, setViewerImageUrl] = useState([]);
  const [viewerImageIndex, setViewerImageIndex] = useState(0);

  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [selectedAudioUrl, setSelectedAudioUrl] = useState("");

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (open) {
      const delayDebounceFN = setTimeout(() => {
        const fetchMediasList = async () => {
          const { data: { rows: imagesList } } = await api.get(`/mediasListImages/${contactId}`);
          const { data: { rows: videosList } } = await api.get(`/mediasListVideos/${contactId}`);
          const { data: { rows: audiosList } } = await api.get(`/mediasListAudios/${contactId}`);
          const { data: { rows: othersList } } = await api.get(`/mediasListOthers/${contactId}`);
          setMediasListImages(imagesList);
          setMediasListVideos(videosList);
          setMediasListAudios(audiosList);
          setMediasListOthers(othersList);

          const images = [];
          if (imagesList.length > 0) imagesList.forEach(image => images.push({ src: image.mediaUrl, downloadUrl: image.mediaUrl }));
          setViewerImageUrl(images);
        };

        fetchMediasList();
      }, 500);

      return () => clearTimeout(delayDebounceFN);
    }
    else {
      setMediasListImages([]);
      setMediasListVideos([]);
      setMediasListAudios([]);
      setMediasListOthers([]);

      setViewerVisible(false);
      setViewerImageUrl([]);
      setViewerImageIndex(0);
      
      setAudioModalOpen(false);
      setSelectedAudioUrl("");
    }
  }, [open, contactId]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => { 
    setMediasListImages([]);
    setMediasListVideos([]);
    setMediasListAudios([]);
    setMediasListOthers([]);

    setMediasTab("images");

    setViewerVisible(false);
    setViewerImageUrl([]);
    setViewerImageIndex(0);
    
    setAudioModalOpen(false);
    setSelectedAudioUrl("");

    onClose();
  };

  const handleOpenAudioModal = (audioUrl) => {
    setAudioModalOpen(true);
    setSelectedAudioUrl(audioUrl);
  };

  const handleCloseAudioModal = () => { 
    setAudioModalOpen(false);
    setSelectedAudioUrl("");
  };

  const handleChange = (_event, newTab) => {
    setMediasTab(newTab);
  };

  // ***---- Render Sub Components ----***
  const extractTooltipTitle = (mediaUrl) => {
    return mediaUrl.split("/").pop();
  };

  const renderImage = (image, index) => {
    if (!image.mediaUrl) return (<></>);

    return (
      <Tooltip title={extractTooltipTitle(image.mediaUrl)} placement="top-start" arrow>
        <img
          key={image.id}
          className={classes.mediaItem}
          src={image.mediaUrl}
          alt="" 
          onClick={() => {
            setViewerVisible(true);
            setViewerImageIndex(index);
          }}
        />
      </Tooltip>
    );
  };

  const renderVideo = (video) => {
    if (!video.mediaUrl) return (<></>);

    return (
      <Tooltip title={extractTooltipTitle(video.mediaUrl)} placement="top-start" arrow>
        <a href={video.mediaUrl} target="_blank" rel="noreferrer">
          <span key={video.id} className={classes.mediaItemSpan}>
            <img className={classes.fileIcon} src={videoIcon} alt="" />
          </span>
        </a>
      </Tooltip>
    );
  };

  const renderAudio = (audio) => {
    if (!audio.mediaUrl) return (<></>);

    return (
      <Tooltip title={extractTooltipTitle(audio.mediaUrl)} placement="top-start" arrow>
        <span key={audio.id} className={classes.mediaItemSpan} onClick={() => handleOpenAudioModal(audio.mediaUrl)}>
          <img className={classes.fileIcon} src={audioIcon} alt="" />
        </span>
      </Tooltip>
    );
  };

  const renderOther = (other) => {
    if (!other.mediaUrl) return (<></>);

    return (
      <Tooltip title={extractTooltipTitle(other.mediaUrl)} placement="top-start" arrow>
        <a href={other.mediaUrl} target="_blank" rel="noreferrer">
          <span key={other.id} className={classes.mediaItemSpan}>
            <img className={classes.fileIcon} src={docIcon} alt="" />
          </span>
        </a>
      </Tooltip>
    );
  };

  const renderMediasList = (selectedTab) => {
    const shouldRenderImages = selectedTab === "images" && mediasListImages.length > 0;
    const shouldRenderVideos = selectedTab === "videos" && mediasListVideos.length > 0;
    const shouldRenderAudios = selectedTab === "audios" && mediasListAudios.length > 0;
    const shouldRenderOthers = selectedTab === "others" && mediasListOthers.length > 0;

    return (
      <div className={classes.mediasList}>
        {shouldRenderImages && mediasListImages.map((image, index) => renderImage(image, index))}
        {shouldRenderVideos && mediasListVideos.map((video, _index) => renderVideo(video))}
        {shouldRenderAudios && mediasListAudios.map((audio, _index) => renderAudio(audio))}
        {shouldRenderOthers && mediasListOthers.map((other, _index) => renderOther(other))}
      </div>
    );
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <Viewer
				downloadable={true}
				downloadInNewWindow={true}
				images={viewerImageUrl}
				onClose={() => { setViewerVisible(false); } }
				onMaskClick={() => { setViewerVisible(false); }}
				visible={viewerVisible}
				zIndex={10000}
        activeIndex={viewerImageIndex}
			/>

      <AudioGalleryModal
        open={audioModalOpen}
        onClose={handleCloseAudioModal}
        audioUrl={selectedAudioUrl}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
        <DialogTitle>
          <span>{i18n.t("mediasGalleryModal.title.up")}</span>
        </DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
          <Tabs
            value={mediasTab}
            onChange={handleChange}
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={i18n.t("mediasGalleryModal.tabBar.images")} value="images" />
            <Tab label={i18n.t("mediasGalleryModal.tabBar.videos")} value="videos" />
            <Tab label={i18n.t("mediasGalleryModal.tabBar.audios")} value="audios" />
            <Tab label={i18n.t("mediasGalleryModal.tabBar.others")} value="others" />
          </Tabs>

          {renderMediasList(mediasTab)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediasGalleryModal;